import { DocumentIcon, ExternalLinkIcon } from '@heroicons/react/outline';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useModal } from "../contexts";
import useScript from '../hooks/useScript';
import useGoogleAccessToken from '../hooks/useGoogleAccessToken';
import { BLOCK_ICON_MAP, GOOGLE_MIMETYPE_MAP } from '../utils';
import ErrorModal from './ErrorModal';
import GooglePickerButton, { Ref as GooglePickerRef } from "./GooglePickerButton";
import LoadingModal from './LoadingModal';

interface File {
  id: string;
  mimeType: string;
  name: string
}

interface DocumentLink {
  webViewLink: string;
  name: string;
}

const SCOPES = ["https://www.googleapis.com/auth/drive.file"];

function ConvertLanding() {
  const status = useScript('https://apis.google.com/js/api.js');
  const { data: accessToken } = useGoogleAccessToken(SCOPES);
  const googlePickerRef = useRef<GooglePickerRef | null>(null);

  const [clientLoaded, setClientLoaded] = useState(false);
  const [file, setFile] = useState<DocumentLink>();

  const { openModal, closeModal } = useModal();

  // Prevent new user modal from showing
  useEffect(() => {
    if (!localStorage.getItem("hasNewUserModalShown"))
      localStorage.setItem("hasNewUserModalShown", "true");
  }, []);

  useEffect(() => {
    if (status !== "ready")
      return;

    // @ts-ignore
    const gapi = window.gapi;

    gapi.load('client',
      () => setClientLoaded(true)
    );
  }, [status]);

  function openGooglePicker() {
    googlePickerRef?.current?.openGooglePicker();
  }

  async function handleSelectFile(pickerResult: File) {
    if (!clientLoaded)
      return;

    if (pickerResult.mimeType !== "application/pdf")
      return openModal(<ErrorModal details="This conversion only works with PDF files. Please make sure you've selected a PDF file to continue." />)

    openModal(<LoadingModal title="Converting PDF to Google Docs file" subtitle="This may take a minute as we create the document in your Google&nbsp;Drive." />);

    // @ts-ignore
    const gapi = window.gapi;

    await gapi.client.init({
      "accessToken": accessToken,
      "apiKey": process.env.REACT_APP_GOOGLE_API_KEY,
      "clientId": process.env.REACT_APP_GOOGLE_CLIENT_ID,
      "discoveryDocs": ["https://www.googleapis.com/discovery/v1/apis/drive/v3/rest"],
      "scope": SCOPES[0],
    });

    try {
      // https://stackoverflow.com/questions/49313477/using-drive-api-driveapp-to-convert-from-pdfs-to-google-documents
      const response = await gapi.client.request({
        path: `/drive/v3/files/${pickerResult.id}/copy`,
        params: { "fields": "webViewLink,name,exportLinks" },
        method: "POST",
        body: {
          name: `[PORTANT] ${pickerResult.name}`,
          mimeType: GOOGLE_MIMETYPE_MAP["GOOGLE_DOCS"]
        }
      });

      const data = JSON.parse(response.body);

      setFile({
        webViewLink: data["webViewLink"],
        name: data["name"]
      });

      closeModal();
    } catch (error) {
      openModal(<ErrorModal details={JSON.stringify(error)} />)
    }
  }

  let content: ReactNode;
  if (file)
    content = (
      <div className='bg-white w-[600px] m-auto p-8 rounded flex flex-col gap-6 shadow'>
        <div className='font-semibold font-gilroy'>Document Converted ✅</div>
        <a className='bg-gray-100 flex justify-between items-center p-4 rounded gap-5' href={file.webViewLink} target="_blank" rel="noreferrer">
          <div className='flex gap-4 items-center truncate'>
            <img src={BLOCK_ICON_MAP["GOOGLE_DOCS"]} className="w-8 h-8" alt="Google docs icon" />
            <div className='text-sm truncate font-semibold font-gilroy text-gray-600'>{file.name}</div>
          </div>
          <button className='btn btn-blue flex gap-2'>
            Open
            <ExternalLinkIcon className="h-5 w-5 mb-1" />
          </button>
        </a>
        <div className='flex flex-col gap-1'>
          <div className='font-semibold text-blue font-gilroy'>What is Portant?</div>
          <div className='text-sm text-gray-500'>A free document automation tool for contracts, proposals, invoices and more...</div>
          <div className='text-sm text-gray-500'>✍️ Request contract signatures</div>
          <div className='text-sm text-gray-500'>📄 Bulk generate documents like invoices</div>
          <div className='text-sm text-gray-500 flex'>
            <img src={BLOCK_ICON_MAP["GOOGLE_DOCS"]} className="w-4 h-4 mr-[3px]" alt="Google docs icon" />
            Google Docs & Slides templates
          </div>
        </div>
        <a href="/" className='btn btn-white' target='_blank' onClick={() => localStorage.removeItem("hasNewUserModalShown")}>Try it now for free</a>
      </div>
    )
  else
    content = (
      <GooglePickerButton className="btn btn-blue m-auto" blockType="PDF" onPicked={handleSelectFile} onLoad={openGooglePicker} onAuth={openGooglePicker} ref={googlePickerRef} scopes={SCOPES} displayUploadTab displayUploadTabFirst>
        <><DocumentIcon className="w-6 h-6 mr-2" />Select PDF File</>
      </GooglePickerButton>
    )

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {content}
    </div>
  );
}

export default ConvertLanding;
